/**
 * KpiTile — canonical "label + numeric value + optional delta" tile.
 *
 * Mockup parity: every KPI-style metric in
 * `reignara_redesign/app/dashboard/page.tsx` (Active Sponsors, Pending
 * Expenses, Days Until Pageant, Total Submitted, etc.) renders the
 * same three-part composite — eyebrow label, italic display numeric,
 * optional delta line. The mockup uses `font-display `
 * `text-[26-32px]` slate-teal numerics on tan-tinted cards and
 * ink-foreground numerics on white cards.
 *
 * Variants
 * --------
 *   - `default` : white card, ink numeric — neutral metric tile.
 *   - `tinted`  : tan-tinted card (`bg-surface-tinted`), slate-teal
 *                 numeric — used to emphasize a hero metric on the
 *                 page (e.g. "Total Reimbursed").
 *   - `alert`   : destructive-muted card, destructive-text numeric —
 *                 used when a metric represents an open issue
 *                 (e.g. "Pending Expenses awaiting review").
 *
 * Tone
 * ----
 *   - `neutral` (default): plain caption.
 *   - `up`      : success-text caption (positive trend).
 *   - `down`    : destructive-text caption (negative trend).
 *
 * The delta caption is optional; when omitted the tile reserves no
 * extra vertical space.
 *
 * Spec: meridian-redesign — PR (m.6) Phase 1
 * Requirements: 1.5, 18.5
 */

import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type KpiTileVariant = 'default' | 'tinted' | 'alert';
export type KpiTileTone = 'neutral' | 'up' | 'down';

export interface KpiTileProps {
  /** Eyebrow label (uppercase, tracking, muted). */
  label: string;
  /** Display value — string OR ReactNode. Rendered italic display-Bavex. */
  value: ReactNode;
  /** Optional caption / delta line below the value. */
  caption?: ReactNode;
  /** Optional inline action affordance (e.g. an arrow button). */
  action?: ReactNode;
  /** Visual variant — defaults to `default`. */
  variant?: KpiTileVariant;
  /** Caption tone — defaults to `neutral`. */
  tone?: KpiTileTone;
  /** Extra classes merged via `cn()`. */
  className?: string;
  /** Stable test id for property tests. */
  'data-testid'?: string;
}

const VARIANT_CARD: Record<KpiTileVariant, string> = {
  default: 'bg-card border-border',
  tinted: 'bg-surface-tinted border-border',
  alert: 'bg-destructive-muted border-destructive/20',
};

const VARIANT_VALUE: Record<KpiTileVariant, string> = {
  default: 'text-foreground',
  tinted: 'text-tertiary',
  alert: 'text-destructive-text',
};

const TONE_CAPTION: Record<KpiTileTone, string> = {
  neutral: 'text-muted-foreground',
  up: 'text-success-text',
  down: 'text-destructive-text',
};

export function KpiTile({
  label,
  value,
  caption,
  action,
  variant = 'default',
  tone = 'neutral',
  className,
  'data-testid': dataTestId,
}: KpiTileProps): JSX.Element {
  return (
    <div
      data-testid={dataTestId ?? 'kpi-tile'}
      data-kpi-variant={variant}
      className={cn(
        'rounded-card border p-card-comfortable shadow-subtle',
        VARIANT_CARD[variant],
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-eyebrow-sm text-muted-foreground">
          {label}
        </span>
        {action ? <div className="shrink-0 -mt-0.5">{action}</div> : null}
      </div>
      <p
        data-testid="kpi-tile-value"
        className={cn(
          // `italic` is applied directly here in addition to the
          // `@layer components` rule on `.text-kpi*` (see
          // `frontend/src/index.css`). The layer rule is the
          // canonical source of truth; the inline utility is a
          // belt-and-suspenders guarantee that survives any future
          // refactor or build-tool change. Italic discipline (m.6)
          // requires KPI numerals to render italic Bavex.
          'mt-2 font-display italic text-kpi sm:text-kpi-lg',
          VARIANT_VALUE[variant],
        )}
      >
        {value}
      </p>
      {caption ? (
        <p
          data-testid="kpi-tile-caption"
          className={cn(
            'mt-1 font-sans text-body-sm',
            TONE_CAPTION[tone],
          )}
        >
          {caption}
        </p>
      ) : null}
    </div>
  );
}

export default KpiTile;
